import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { commonMethods } from 'Utils/commonMethods'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'

const MarketSettings = ({ setLoading, index, setChangesMade }) => {
  const { t } = useTranslation()

  const [submitButtonDisable, setSubmitButtonDisable] = useState(false)
  const [editSettings, setEditSettings] = useState({
    editMode: false,
    submitButton: false,
    editIcon: true
  })
  const [settingsData, setSettingsData] = useState({})
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  useEffect(() => {
    getAllAppSetting()
  }, [])

  const getAllAppSetting = async () => {
    setLoading(true)

    try {
      const response = await apiAdapterCoffeeWeb.appSetting()
      const data = response?.data

      setSettingsData(data)
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('NO_DATA_FOUND'),
        onRetry: commonMethods.handleRetry,
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)

    try {
      const updateMarketTime = {
        rcMarketStartTime: convertToTimeString(settingsData?.rcMarketStartTime),
        rcMarketEndTime: convertToTimeString(settingsData?.rcMarketEndTime),
        kcMarketStartTime: convertToTimeString(settingsData?.kcMarketStartTime),
        kcMarketEndTime: convertToTimeString(settingsData?.kcMarketEndTime)
      }

      await apiAdapterCoffeeWeb.updateMarketTimings(updateMarketTime)

      setEditSettings({
        editMode: false,
        submitButton: false,
        editIcon: true
      })

      setChangesMade(false)

      setShowDialog({
        showModal: true,
        modalType: 'success',
        message: t('MARKET_TIME_UPDATED_SUCCESSFULLY'),
        onHide: handleDismiss,
        onSuccess: handleDismiss
      })
    } catch {
      setShowDialog({
        showModal: true,
        modalType: 'error',
        message: t('SOMETHING_WENT_WRONG'),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const convertToDate = (timeValue) => {
    if (!timeValue) return null

    if (timeValue instanceof Date) {
      return timeValue
    }

    if (typeof timeValue === 'string') {
      const [hours, minutes] = timeValue.split(':')
      const date = new Date()

      date.setHours(Number(hours))
      date.setMinutes(Number(minutes))
      date.setSeconds(0)

      return date
    }

    return null
  }

  const convertToTimeString = (value) => {
    if (!value) return ''

    if (typeof value === 'string') {
      return value
    }

    if (value instanceof Date) {
      const hours = String(value.getHours()).padStart(2, '0')
      const minutes = String(value.getMinutes()).padStart(2, '0')

      return `${hours}:${minutes}`
    }

    return ''
  }

  const handleTimeChange = (field, value) => {
    setSettingsData((prev) => ({
      ...prev,
      [field]: value
    }))
    setChangesMade(true)
    setSubmitButtonDisable(false)
  }

  const handleEditButtonClick = () => {
    setEditSettings({
      editMode: !editSettings.editMode,
      editIcon: false,
      submitButton: true
    })

    setSubmitButtonDisable(true)
  }

  const handleReset = () => {
    setEditSettings({
      editMode: false,
      submitButton: false,
      editIcon: true
    })
    setShowDialog({
      ...showDialog,
      showModal: false
    })

    getAllAppSetting()
  }

  const handleResetConfirmationOpen = () => {
    document.body.style.overflow = 'hidden'
    setShowDialog({
      ...showDialog,
      showModal: true,
      modalType: 'info',
      message: t('ARE_YOU_SURE_YOU_WANT_TO_PROCEED'),
      onConfirmation: handleReset,
      onHide: handleDismiss
    })
  }

  const handleDismiss = () => {
    setShowDialog({
      ...showDialog,
      showModal: false
    })
  }

  return (
    <>
      <div className="market-settings-container">
        <div className="edit-container">
          {editSettings.editMode ? (
            <ButtonComponent type={'reset'} onClick={handleResetConfirmationOpen} />
          ) : (
            <>
              <ButtonComponent type={'edit'} onClick={handleEditButtonClick} />
            </>
          )}
        </div>

        <div className="time-picker-container">
          <div className="time-field">
            <label htmlFor="rcStart">{t('RC_MARKET_START_TIME')}</label>
            <Calendar id="rcStart" value={convertToDate(settingsData?.rcMarketStartTime)} onChange={(e) => handleTimeChange('rcMarketStartTime', e.value)} timeOnly hourFormat="24" disabled={!editSettings.editMode} placeholder="Select Time" />
          </div>

          <div className="time-field">
            <label htmlFor="rcEnd">{t('RC_MARKET_END_TIME')}</label>
            <Calendar id="rcEnd" value={convertToDate(settingsData?.rcMarketEndTime)} onChange={(e) => handleTimeChange('rcMarketEndTime', e.value)} timeOnly hourFormat="24" disabled={!editSettings.editMode} placeholder="Select Time" />
          </div>

          <div className="time-field">
            <label htmlFor="kcStart">{t('KC_MARKET_START_TIME')}</label>
            <Calendar id="kcStart" value={convertToDate(settingsData?.kcMarketStartTime)} onChange={(e) => handleTimeChange('kcMarketStartTime', e.value)} timeOnly hourFormat="24" disabled={!editSettings.editMode} placeholder="Select Time" />
          </div>

          <div className="time-field">
            <label htmlFor="kcEnd">{t('KC_MARKET_END_TIME')}</label>
            <Calendar id="kcEnd" value={convertToDate(settingsData?.kcMarketEndTime)} onChange={(e) => handleTimeChange('kcMarketEndTime', e.value)} timeOnly hourFormat="24" disabled={!editSettings.editMode} placeholder="Select Time" />
          </div>
        </div>
        <div className="button-container">
          {editSettings.submitButton && (
            <div className="button-group">
              <ButtonComponent type={'cancel'} onClick={handleReset} />
              <ButtonComponent type={'submit'} onClick={handleSubmit} disabled={submitButtonDisable} />
            </div>
          )}
        </div>
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </>
  )
}

export default Loader(MarketSettings)
